import mitt from "mitt";
import type { z } from "zod";

import type { DiContainer } from "../model";

import { AdminPanelApiProvider, AdminPanelService } from "~/entities/admin-panel/@x/di";
import { AuthApiProvider, AuthService } from "~/entities/auth/@x/di";
import type { EventBusEvent } from "~/entities/event-bus/@x/di";
import { ImageApiProvider, ImageService } from "~/entities/image/@x/di";
import { InquiryApiProvider, InquiryService } from "~/entities/inquiry/@x/di";
import { NotificationApiProvider, NotificationService } from "~/entities/notification/@x/di";
import { OrderApiProvider, OrderService } from "~/entities/order/@x/di";
import { ReviewApiProvider, ReviewService } from "~/entities/review/@x/di";
import {
  ServiceCategoryApiProvider,
  ServiceCategoryService,
} from "~/entities/service-category/@x/di";
import { ServiceApiProvider, ServiceService } from "~/entities/service/@x/di";
import { UserRoleApiProvider, UserRoleService } from "~/entities/user-role/@x/di";
import { UserApiProvider, UserService } from "~/entities/user/@x/di";
import { createAxios } from "~/shared/api";
import type { Config } from "~/shared/config";
import { createUrl } from "~/shared/http";

type Params<
  UserRoleKey extends z.KeySchema,
  UserRoleValue extends z.ZodType<string, z.ZodTypeDef>,
> = {
  config: Config<UserRoleKey, UserRoleValue>;
  getLocale: Parameters<typeof createAxios>[0]["getLocale"];
};

function initDiContainer<
  UserRoleKey extends z.KeySchema,
  UserRoleValue extends z.ZodType<string, z.ZodTypeDef>,
>({ config, getLocale }: Params<UserRoleKey, UserRoleValue>): DiContainer {
  const {
    backendApp: { protocol, address, port, apiBaseUrl },
  } = config;

  const eventBus = mitt<EventBusEvent>();

  const axios = createAxios({
    baseUrl: createUrl({
      protocol,
      address,
      port,
      baseUrl: apiBaseUrl,
    }),
    interceptors: {
      response: [
        {
          onFulfilled: (value) => {
            if (value.status === 401) {
              eventBus.emit("userNotAuthenticated");
            }

            return value;
          },
        },
      ],
    },
    getLocale,
    validateStatus: null,
  });

  return {
    serviceService: new ServiceService(new ServiceApiProvider(axios)),
    orderService: new OrderService(new OrderApiProvider(axios)),
    adminPanelService: new AdminPanelService(new AdminPanelApiProvider(axios)),
    serviceCategoryService: new ServiceCategoryService(new ServiceCategoryApiProvider(axios)),
    authService: new AuthService(new AuthApiProvider(axios)),
    userService: new UserService(new UserApiProvider(axios)),
    userRoleService: new UserRoleService(new UserRoleApiProvider(axios)),
    inquiryService: new InquiryService(new InquiryApiProvider(axios)),
    reviewService: new ReviewService(new ReviewApiProvider(axios)),
    notificationService: new NotificationService(new NotificationApiProvider(axios)),
    imageService: new ImageService(new ImageApiProvider(axios)),
    eventBus,
  };
}

export { initDiContainer };
